// Backend client. Prefers a real ScaiKey Bearer token (set after OIDC login);
// falls back to `X-Dev-User-Id` from the env var when no token is present —
// useful in dev before a real ScaiKey mock is wired up. Production builds
// should not include `VITE_DEV_USER_ID`.

import { accessToken, refreshIfNeeded } from "./auth";
import type { ApiError } from "./types";

const DEV_USER_ID = import.meta.env.VITE_DEV_USER_ID ?? "";

export class ApiException extends Error {
  status: number;
  code: string;
  details: Record<string, unknown>;
  traceId: string | null;

  constructor(status: number, payload: ApiError) {
    super(payload.error.message);
    this.status = status;
    this.code = payload.error.code;
    this.details = payload.error.details ?? {};
    this.traceId = payload.error.trace_id ?? null;
  }
}

export type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  ifMatch?: number;
  idempotencyKey?: string;
};

function buildQuery(query: RequestOptions["query"]): string {
  if (!query) return "";
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null) continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}

export async function apiRequest<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  // Prefer real Bearer token; fall back to dev header if no token.
  const tok = await refreshIfNeeded().catch(() => accessToken());
  const bearer = tok ?? accessToken();
  if (bearer) {
    headers["Authorization"] = `Bearer ${bearer}`;
  } else if (DEV_USER_ID) {
    headers["X-Dev-User-Id"] = DEV_USER_ID;
  }
  if (opts.ifMatch !== undefined) headers["If-Match"] = String(opts.ifMatch);
  if (opts.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;

  let bodyToSend: BodyInit | undefined;
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
    bodyToSend = JSON.stringify(opts.body);
  }

  const res = await fetch(`${path}${buildQuery(opts.query)}`, {
    method: opts.method ?? "GET",
    headers,
    body: bodyToSend,
  });

  if (res.status === 204) {
    return undefined as unknown as T;
  }

  const text = await res.text();
  const data = text ? JSON.parse(text) : null;

  if (!res.ok) {
    throw new ApiException(res.status, data as ApiError);
  }

  return data as T;
}

/**
 * Extract the ScaiControl request_id + named error code from a failed
 * CRM API call, when the underlying error originated upstream at
 * ScaiControl. Returns `null` when this isn't a ScaiControl-attributable
 * failure — callers fall back to the raw message.
 *
 * The backend stuffs these into the ScaiCRMError `details` block via
 * `_raise_for_scaicontrol`; this helper just reads them back out on the
 * UI side so reps can copy/paste a single token at support.
 */
export type ScaiControlErrorReference = {
  code: string | null;
  request_id: string | null;
};

export function scaicontrolReference(err: unknown): ScaiControlErrorReference | null {
  if (!(err instanceof ApiException)) return null;
  const code = err.details.scaicontrol_code;
  const requestId = err.details.scaicontrol_request_id;
  if (!code && !requestId) return null;
  return {
    code: typeof code === "string" ? code : null,
    request_id: typeof requestId === "string" ? requestId : null,
  };
}
